import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Button, Chip, Container, Grid, Link, Stack, SwipeableDrawer, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import { getLocations } from '../../../redux/slices/settingsManagementRedux/locationsRedux';
import { getJobRoles } from '../../../redux/slices/recruitmentRedux/jobRolesRedux';
import { addJobPost, getJobPosts, updateJobPost } from '../../../redux/slices/recruitmentRedux/jobPostsRedux';
import { accessVerify } from '../../../utils/common';
import PermissionRequired from '../../errorPages/permissionRequired';

const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk In' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
];

const dataModel = {
  id: null,
  jobRoleId: '',
  title: '',
  platform: 'facebook',
  postUrl: '',
  postContent: '',
  locationId: '',
  vacancies: 1,
  publishedDate: '',
  closingDate: '',
  status: 'draft',
  notes: '',
};

export default function JobPosts() {
  const { themeStretch } = useSettings();
  const [selectedObject, setSelectedObject] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState(false);

  const { data, addData } = useSelector((state) => state.recruitmentJobPosts);
  const { data: jobRoles } = useSelector((state) => state.recruitmentJobRoles);
  const { data: locations } = useSelector((state) => state.location);

  useEffect(() => {
    dispatch(getJobPosts());
    dispatch(getJobRoles());
    dispatch(getLocations());
  }, []);

  const JobPostSchema = Yup.object().shape({
    jobRoleId: Yup.string().required('Job role is required'),
    title: Yup.string().required('Post title is required'),
    platform: Yup.string().required('Platform is required'),
    postUrl: Yup.string().nullable().url('Enter a valid URL'),
    postContent: Yup.string().nullable(),
    locationId: Yup.string().nullable(),
    vacancies: Yup.number().required('Vacancies are required').min(1, 'At least one vacancy is required'),
    publishedDate: Yup.string().nullable(),
    closingDate: Yup.string().nullable(),
    status: Yup.string().required('Status is required'),
    notes: Yup.string().nullable(),
  });

  const defaultValues = useMemo(
    () => ({
      jobRoleId: selectedObject?.jobRoleId || '',
      title: selectedObject?.title || '',
      platform: selectedObject?.platform || 'facebook',
      postUrl: selectedObject?.postUrl || '',
      postContent: selectedObject?.postContent || '',
      locationId: selectedObject?.locationId || '',
      vacancies: selectedObject?.vacancies || 1,
      publishedDate: selectedObject?.publishedDate ? moment(selectedObject.publishedDate).format('YYYY-MM-DD') : '',
      closingDate: selectedObject?.closingDate ? moment(selectedObject.closingDate).format('YYYY-MM-DD') : '',
      status: selectedObject?.status || 'draft',
      notes: selectedObject?.notes || '',
    }),
    [selectedObject]
  );

  const methods = useForm({
    resolver: yupResolver(JobPostSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getJobPosts());
      setIsModelOpen(false);
    }
  }, [addData]);

  const manageModel = (modelData, type) => {
    setIsModelOpen(true);
    setIsAdd(type === 'add');
    setSelectedObject(type === 'add' ? dataModel : modelData);
  };

  const onSubmit = async (value) => {
    const payload = {
      jobRoleId: value?.jobRoleId || '',
      title: value?.title || '',
      platform: value?.platform || '',
      postUrl: value?.postUrl || null,
      postContent: value?.postContent || '',
      locationId: value?.locationId || null,
      vacancies: value?.vacancies || 1,
      publishedDate: value?.publishedDate || null,
      closingDate: value?.closingDate || null,
      status: value?.status || 'draft',
      notes: value?.notes || '',
    };

    if (isAdd) {
      dispatch(addJobPost(payload));
    } else {
      dispatch(updateJobPost({ ...payload, id: selectedObject?.id }));
    }
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Job Post',
    },
    {
      accessorKey: 'jobRoleName',
      header: 'Job Role',
    },
    {
      accessorKey: 'platform',
      header: 'Platform',
      Cell: ({ cell }) => PLATFORM_OPTIONS.find((item) => item.value === cell.getValue())?.label || cell.getValue(),
    },
    {
      accessorKey: 'locationName',
      header: 'Location',
    },
    {
      accessorKey: 'vacancies',
      header: 'Vacancies',
    },
    {
      accessorKey: 'publishedDate',
      header: 'Published',
      Cell: ({ cell }) => (cell.getValue() ? moment(cell.getValue()).format('YYYY-MM-DD') : ''),
    },
    {
      accessorKey: 'closingDate',
      header: 'Closing',
      Cell: ({ cell }) => (cell.getValue() ? moment(cell.getValue()).format('YYYY-MM-DD') : ''),
    },
    {
      accessorKey: 'postUrl',
      header: 'URL',
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <Link href={cell.getValue()} target='_blank' rel='noreferrer' onClick={(event) => event.stopPropagation()}>
            Open
          </Link>
        ) : (
          ''
        ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => (
        <Chip
          size='small'
          color={cell.getValue() === 'published' ? 'success' : cell.getValue() === 'closed' ? 'default' : 'warning'}
          label={STATUS_OPTIONS.find((item) => item.value === cell.getValue())?.label || cell.getValue()}
        />
      ),
    },
  ];

  return (
    <Page title='Recruitment Job Posts'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Job Posts'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Recruitment', href: PATH_DASHBOARD.recruitment.root }, { name: 'Job Posts' }]}
          action={
            <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageModel(dataModel, 'add')}>
              New Job Post
            </Button>
          }
        />

        {accessVerify('GENERAL_DATA') ? (
          <DataGridTable
            name='Recruitment Job Posts'
            data={data || []}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            isRowClickable={true}
            onRowClick={(row) => manageModel(row?.original, 'edit')}
            renderRowActionItems={() => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <SwipeableDrawer
            anchor='top'
            open={isModelOpen}
            onOpen={() => setIsModelOpen(true)}
            onClose={() => setIsModelOpen(false)}
            PaperProps={{
              sx: {
                width: '90%',
                height: '90%',
                marginLeft: '5%',
                marginTop: '3%',
                borderRadius: '10px',
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 3 }}>
                <Typography variant='h5'>{isAdd ? 'Add Job Post' : 'Edit Job Post'}</Typography>
                <Button color='inherit' startIcon={<Iconify icon='eva:close-fill' />} onClick={() => setIsModelOpen(false)}>
                  Close
                </Button>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    <Box sx={{ p: 3, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <Typography variant='subtitle1' padding='0 0 16px'>
                        Post Information
                      </Typography>
                      <Stack spacing={3}>
                        <RHFSelect name='jobRoleId' label='Job Role'>
                          <option value='' />
                          {(jobRoles || []).map((jobRole) => (
                            <option key={jobRole.id} value={jobRole.id}>
                              {jobRole.name}
                            </option>
                          ))}
                        </RHFSelect>
                        <RHFTextField name='title' label='Post Title' />
                        <RHFSelect name='platform' label='Platform'>
                          {PLATFORM_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </RHFSelect>
                        <RHFSelect name='locationId' label='Hiring Location'>
                          <option value='' />
                          {(locations || []).map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </RHFSelect>
                        <RHFTextField name='postUrl' label='Post URL' />
                        <RHFTextField name='postContent' label='Post Content' multiline minRows={6} />
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 3, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Stack spacing={3}>
                      <Typography variant='subtitle1'>Schedule</Typography>
                      <RHFTextField name='vacancies' label='Vacancies' type='number' />
                      <RHFTextField name='publishedDate' label='Published Date' type='date' InputLabelProps={{ shrink: true }} />
                      <RHFTextField name='closingDate' label='Closing Date' type='date' InputLabelProps={{ shrink: true }} />
                      <RHFSelect name='status' label='Status'>
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </RHFSelect>
                      <RHFTextField name='notes' label='Internal Notes' multiline minRows={4} />
                      <LoadingButton type='button' variant='contained' size='large' loading={isSubmitting} fullWidth onClick={handleSubmit(onSubmit)}>
                        {isAdd ? 'Create Job Post' : 'Save Changes'}
                      </LoadingButton>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </SwipeableDrawer>
        </FormProvider>
      </Container>
    </Page>
  );
}
